import { Button } from "@/components/ui/button";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import BodyPortal from "@/components/BodyPortal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type Locale = "en" | "fr" | "es";

// Ember Orange brand colour — not in default Tailwind palette
const EMBER = "#FF6B35";

export default function Navbar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [retailersExpanded, setRetailersExpanded] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener("closeMenu", handler);
    return () => window.removeEventListener("closeMenu", handler);
  }, []);

  // Language context (text only; path-based locale is derived below)
  let lang: any = {};
  try { lang = useLanguage?.() || {}; } catch {}
  const t = lang.t ?? ((k: string, d?: string) => d ?? k);
  const setLanguage = lang.setLanguage ?? (() => {});

  // Detect locale from current path prefix
  const locale: Locale = useMemo<Locale>(() => {
    if (pathname.startsWith("/fr")) return "fr";
    if (pathname.startsWith("/es")) return "es";
    return "en";
  }, [pathname]);

  // Per-locale route map. news/demo kept for language-switch path mapping only.
  const routes = {
    // These three had locales pointing at the ENGLISH route even though a
    // translated route existed. The Spanish homepage was the worst case: with
    // home.es = "/", nothing on the site ever linked to /es, so it was a total
    // orphan — unreachable by crawl while still being declared as the Spanish
    // alternate in hreflang and listed in the sitemap.
    home:       { en: "/",                    fr: "/fr",                      es: "/es"                      },
    howItWorks: { en: "/how-it-works",        fr: "/fr/comment-ca-marche",    es: "/es/como-funciona"        },
    retailers:        { en: "/retailers",          fr: "/fr/detaillants",          es: "/es/minoristas"           },
    manufacturers:    { en: "/manufacturers",      fr: "/fr/fabricants",           es: "/es/fabricantes"          },
    retailersPricing: { en: "/retailers/pricing", fr: "/fr/detaillants/tarifs",   es: "/es/minoristas/precios"   },
    retailersDemo:    { en: "/retailers/demo",   fr: "/fr/detaillants/demo",     es: "/es/minoristas/demo"      },
    about:      { en: "/about",               fr: "/fr/a-propos",             es: "/es/sobre-nosotros"       },
    contact:    { en: "/contact",             fr: "/fr/contact",              es: "/es/contacto"             },
    whyFyrris:  { en: "/why-fyrris",          fr: "/fr/pourquoi-fyrris",      es: "/why-fyrris"              },
    faq:        { en: "/faq",                 fr: "/fr/faq",                  es: "/faq"                     },
    news:       { en: "/news",                fr: "/fr/nouvelles",            es: "/news"                    },
    demo:       { en: "/ar-demo",             fr: "/fr/ar-demo",              es: "/ar-demo"                 },
    signin:     { en: "/signin",              fr: "/signin",                  es: "/signin"                  },
    signup:     { en: "/signup",              fr: "/fr/inscription",          es: "/es/registro"             },
  } as const;

  const lp = (key: keyof typeof routes) => routes[key][locale] ?? routes[key].en;

  // Path ↔ locale mapping used when switching languages
  const pathMap = [
    { en: /^\/$/, fr: /^\/fr\/?$/, es: /^\/es\/?$/ },
    { en: /^\/how-it-works\/?$/, fr: /^\/fr\/comment-ca-marche\/?$/, es: /^\/es\/como-funciona\/?$/ },
    { en: /^\/news\/?$/, fr: /^\/fr\/nouvelles\/?$/, es: /^\/news\/?$/ },
    { en: /^\/ar-demo\/?$/, fr: /^\/fr\/ar-demo\/?$/, es: /^\/ar-demo\/?$/ },
    { en: /^\/retailers\/?$/, fr: /^\/fr\/detaillants\/?$/, es: /^\/retailers\/?$/ },
    { en: /^\/retailers\/pricing\/?$/, fr: /^\/fr\/detaillants\/tarifs\/?$/, es: /^\/es\/minoristas\/precios\/?$/ },
    { en: /^\/retailers\/demo\/?$/,    fr: /^\/fr\/detaillants\/demo\/?$/,   es: /^\/es\/minoristas\/demo\/?$/ },
    { en: /^\/about\/?$/, fr: /^\/fr\/a-propos\/?$/, es: /^\/es\/sobre-nosotros\/?$/ },
    { en: /^\/contact\/?$/, fr: /^\/fr\/contact\/?$/, es: /^\/es\/contacto\/?$/ },
    { en: /^\/why-fyrris\/?$/, fr: /^\/fr\/pourquoi-fyrris\/?$/, es: /^\/why-fyrris\/?$/ },
    { en: /^\/faq\/?$/, fr: /^\/fr\/faq\/?$/, es: /^\/faq\/?$/ },
    { en: /^\/signup\/?$/, fr: /^\/fr\/inscription\/?$/, es: /^\/es\/registro\/?$/ },
  ];

  const translatePath = (target: Locale) => {
    for (const rule of pathMap) {
      if (locale === "en" && rule.en.test(pathname)) return (routes as any)[Object.keys(routes).find(k => rule.en.test(routes[k as keyof typeof routes].en))!][target];
      if (locale === "fr" && rule.fr.test(pathname)) return (routes as any)[Object.keys(routes).find(k => rule.fr.test(routes[k as keyof typeof routes].fr))!][target];
      if (locale === "es" && rule.es.test(pathname)) return (routes as any)[Object.keys(routes).find(k => rule.es.test(routes[k as keyof typeof routes].es))!][target];
    }
    return routes.home[target];
  };

  const onSwitchLanguage = (target: Locale) => {
    try { setLanguage(target); } catch {}
    navigate(translatePath(target), { replace: false });
  };

  const logoUrl = "/fyrris-logo.png";

  // Locale-aware label helpers
  const label = {
    howItWorks:       t("nav.howItWorks",            locale === "fr" ? "Comment ça marche" : "How It Works"),
    retailers:        t("nav.retailer",              locale === "fr" ? "Détaillants"        : "Retailers"),
    retailersOverview:    t("nav.retailers.overview",    locale === "fr" ? "Vue d'ensemble"  : locale === "es" ? "Descripción general" : "Overview"),
    retailersHowItWorks:  t("nav.retailers.howItWorks",  locale === "fr" ? "Comment ça marche" : locale === "es" ? "Cómo funciona"        : "How It Works"),
    retailersPricing:     t("nav.retailers.pricing",     locale === "fr" ? "Tarifs"             : locale === "es" ? "Precios"              : "Pricing"),
    retailersBookDemo:    t("nav.retailers.bookDemo",    locale === "fr" ? "Réserver une démo"  : locale === "es" ? "Reservar una demo"    : "Book a Demo"),
    retailersCatalogue:   t("nav.retailers.catalogue",   locale === "fr" ? "Notre catalogue"    : locale === "es" ? "Nuestro catálogo"    : "Our Catalogue"),
    about:            t("nav.about",                 locale === "fr" ? "À propos"           : "About"),
    signIn:           t("nav.signIn",                locale === "fr" ? "Se connecter"       : "Sign in"),
    getStarted:       t("nav.getStarted",            locale === "fr" ? "Commencer"          : "Get Started"),
  };

  const Nav = (
    <nav
      id="site-nav"
      className="
        fixed top-0 left-0 w-full h-16
        bg-gray-900/90 backdrop-blur-sm border-b border-gray-800
        text-white z-[2147483647] isolate pointer-events-auto
        flex items-center
      "
    >
      <div className="max-w-7xl mx-auto px-4 w-full flex justify-between items-center">
        {/* Logo → localized home */}
        <img
          src={logoUrl}
          alt="Fyrris"
          onClick={() => navigate(lp("home"))}
          className="cursor-pointer transition-transform hover:scale-105 object-contain h-8 md:h-10 w-auto max-w-[160px]"
        />

        {/* Desktop nav */}
        <div className="hidden md:flex gap-4 items-center pointer-events-auto">
          {/* Primary links */}
          <Button variant="ghost" size="sm" onClick={() => navigate(lp("howItWorks"))}>
            {label.howItWorks}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="flex items-center gap-1">
                {label.retailers}
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={8}
              className="min-w-[160px] bg-gray-800 border-gray-700 text-white"
            >
              <DropdownMenuItem
                onClick={() => navigate(lp("retailers"))}
                className="cursor-pointer focus:bg-gray-700 focus:text-white"
              >
                {label.retailersOverview}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(lp("howItWorks"))}
                className="cursor-pointer focus:bg-gray-700 focus:text-white"
              >
                {label.retailersHowItWorks}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(lp("retailersPricing"))}
                className="cursor-pointer focus:bg-gray-700 focus:text-white"
              >
                {label.retailersPricing}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate(lp("retailersDemo"))}
                className="cursor-pointer focus:bg-gray-700 focus:text-white"
              >
                {label.retailersBookDemo}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" onClick={() => navigate(lp("about"))}>
            {label.about}
          </Button>

          {/* EN · FR · ES
              Rendered as real <Link>s, not buttons. As <button onClick> these
              produced no href, so crawlers saw no path from any page to /fr or
              /es — /es had zero inbound links site-wide despite being in the
              sitemap and hreflang cluster. Anchors also restore middle-click
              and open-in-new-tab. onClick still sets the language so behaviour
              is unchanged for normal clicks. */}
          <div className="flex items-center gap-0.5 text-sm font-medium" aria-label="Change language">
            {(["en", "fr", "es"] as const).map((lc, i) => (
              <span key={lc} className="flex items-center gap-0.5">
                {i > 0 && <span className="text-gray-600">·</span>}
                <Link
                  to={translatePath(lc)}
                  hrefLang={lc}
                  aria-current={locale === lc ? "true" : undefined}
                  onClick={() => setLanguage(lc)}
                  className={`px-1.5 py-0.5 rounded transition-colors ${locale === lc ? "text-white" : "text-gray-400 hover:text-white"}`}
                >
                  {lc.toUpperCase()}
                </Link>
              </span>
            ))}
          </div>

          {/* Sign in (subdued) + Get Started (primary CTA) */}
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-white"
            onClick={() => navigate(lp("signin"))}
          >
            {label.signIn}
          </Button>
          <Button
            size="sm"
            style={{ backgroundColor: EMBER }}
            className="hover:opacity-90 text-white font-semibold"
            onClick={() => navigate(lp("retailersDemo"))}
          >
            {label.getStarted}
          </Button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden pointer-events-auto"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

    </nav>
  );

  const closeDrawer = () => {
    setOpen(false);
    setRetailersExpanded(false);
  };

  const go = (key: keyof typeof routes) => {
    navigate(lp(key));
    closeDrawer();
  };

  return (
    <BodyPortal id="public-nav-root" z={2147483647}>
      <>
        {/* Backdrop — covers everything behind the drawer */}
        {open && (
          <div
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.5)" }}
            onClick={closeDrawer}
            aria-hidden="true"
          />
        )}

        {Nav}

        {/* Mobile side drawer — rendered after Nav so it paints on top at equal z-level */}
        {open && (
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: "80%",
              maxWidth: "320px",
              backgroundColor: "#1A1A2E",
              zIndex: 9999,
              overflowY: "auto",
              padding: "1.5rem",
            }}
          >
            {/* X close button */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "1.5rem" }}>
              <button
                onClick={closeDrawer}
                aria-label="Close menu"
                style={{ color: "#9ca3af", background: "none", border: "none", cursor: "pointer", padding: "4px" }}
              >
                <X size={24} />
              </button>
            </div>

            {/* How It Works */}
            <button
              onClick={() => go("howItWorks")}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "0.625rem 0", color: "#f3f4f6", background: "none", border: "none", cursor: "pointer", fontSize: "1rem", fontWeight: 500 }}
            >
              {label.howItWorks}
            </button>

            {/* Retailers expandable */}
            <div>
              <button
                onClick={() => setRetailersExpanded(!retailersExpanded)}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "0.625rem 0", color: "#f3f4f6", background: "none", border: "none", cursor: "pointer", fontSize: "1rem", fontWeight: 500 }}
              >
                {label.retailers}
                <ChevronDown
                  size={16}
                  style={{ transition: "transform 0.2s", transform: retailersExpanded ? "rotate(180deg)" : "rotate(0deg)", opacity: 0.6 }}
                />
              </button>
              {retailersExpanded && (
                <div style={{ paddingLeft: "1rem", borderLeft: "2px solid #374151", marginLeft: "0.25rem" }}>
                  {[
                    { labelKey: "retailersOverview",   routeKey: "retailers"        },
                    { labelKey: "retailersCatalogue",  routeKey: "manufacturers"    },
                    { labelKey: "retailersHowItWorks", routeKey: "howItWorks"       },
                    { labelKey: "retailersPricing",    routeKey: "retailersPricing" },
                    { labelKey: "retailersBookDemo",   routeKey: "retailersDemo"    },
                  ].map(({ labelKey, routeKey }) => (
                    <button
                      key={routeKey}
                      onClick={() => go(routeKey as keyof typeof routes)}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "0.5rem 0.75rem", color: "#9ca3af", background: "none", border: "none", cursor: "pointer", fontSize: "0.875rem" }}
                    >
                      {label[labelKey as keyof typeof label]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* About */}
            <button
              onClick={() => go("about")}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "0.625rem 0", color: "#f3f4f6", background: "none", border: "none", cursor: "pointer", fontSize: "1rem", fontWeight: 500 }}
            >
              {label.about}
            </button>

            {/* Divider */}
            <div style={{ borderTop: "1px solid #374151", margin: "1rem 0" }} />

            {/* EN · FR · ES */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", paddingBottom: "1rem" }}>
              {(["en", "fr", "es"] as Locale[]).map((loc, i, arr) => (
                <span key={loc} style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                  <button
                    onClick={() => { onSwitchLanguage(loc); closeDrawer(); }}
                    style={{
                      padding: "0.25rem 0.5rem",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: locale === loc ? "#ffffff" : "#6b7280",
                    }}
                  >
                    {loc.toUpperCase()}
                  </button>
                  {i < arr.length - 1 && <span style={{ color: "#4b5563" }}>·</span>}
                </span>
              ))}
            </div>

            {/* Sign In */}
            <button
              onClick={() => go("signin")}
              style={{ display: "block", width: "100%", textAlign: "center", padding: "0.625rem 0", color: "#6b7280", background: "none", border: "none", cursor: "pointer", fontSize: "0.875rem" }}
            >
              {label.signIn}
            </button>

            {/* Get Started CTA */}
            <button
              onClick={() => go("retailersDemo")}
              style={{ display: "block", width: "100%", padding: "0.75rem", marginTop: "0.5rem", backgroundColor: EMBER, color: "#ffffff", border: "none", borderRadius: "0.5rem", cursor: "pointer", fontSize: "1rem", fontWeight: 600, textAlign: "center" }}
            >
              {label.getStarted}
            </button>
          </div>
        )}
      </>
    </BodyPortal>
  );
}
